import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  OffcanvasBodyComponent,
  OffcanvasComponent,
  OffcanvasHeaderComponent
} from '@coreui/angular-pro';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { ConsentComponent, SafeHtmlPipe } from 'shared';

type Policy = 'privacy' | 'terms' | 'imprint';

/**
 * Wraps every funnel page with:
 *   - the light ivory page ground ("Health in the Light")
 *   - top-right language switcher
 *   - bottom legal links (Privacy / TOS / Legal Notice) — open as offcanvas
 *   - company legal-entity strip (uses `login.footer` i18n)
 *
 * The offcanvas + policy-fetch flow mirrors libs/app/.../login-page so the UX
 * matches the rest of the app exactly.
 */
@Component({
  selector: 'app-funnel-shell',
  standalone: true,
  imports: [
    CommonModule,
    OffcanvasComponent,
    OffcanvasHeaderComponent,
    OffcanvasBodyComponent,
    ConsentComponent,
    SafeHtmlPipe,
    TranslatePipe
  ],
  templateUrl: './funnel-shell.component.html',
  styleUrls: ['./funnel-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelShellComponent {
  private i18n             = inject(I18nService);
  private translateService = inject(TranslateService);
  private http             = inject(HttpClient);
  private destroyRef       = inject(DestroyRef);

  /** `login.footer` already contains the company legal-entity HTML. */
  readonly footerText = toSignal(
    this.translateService.translateAsync('login.footer'),
    { initialValue: null }
  );

  // Offcanvas state
  policyVisible    = signal(false);
  policyLoading    = signal(false);
  policyContent    = signal<string>('');

  currentLang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  openPolicy(policy: Policy): void {
    this.openDocument(`/media/${this.currentLang()}/policy/${policy}?embed=true`);
  }

  /**
   * Opens any policy document in the same slide-in panel — e.g. the documents the
   * direct checkout asks the buyer to accept, whose `url` the backend supplies
   * (already `?embed=true`, already in the language it is served in).
   */
  openDocument(url: string): void {
    this.policyLoading.set(true);
    this.policyVisible.set(true);
    this.policyContent.set('');

    this.http.get(url, { responseType: 'text' as 'text' })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (content) => {
        this.policyContent.set(content || '');
        this.policyLoading.set(false);
      },
      error: () => {
        this.policyContent.set('<p>Could not load the policy. Please try again.</p>');
        this.policyLoading.set(false);
      }
    });
  }

  closePolicy(): void {
    this.policyVisible.set(false);
  }

  policyVisibleChange(visible: boolean): void {
    this.policyVisible.set(visible);
  }
}
